/**
 * Baseline: where the client is today, before any change. Gives the report its starting point
 * and gives implementation the figures to measure savings against.
 */
import type { ProductSpec } from "./lever2.ts";
import type { Parcel, Pence } from "./types.ts";
import { round } from "./units.ts";

export interface ServiceBaseline {
  service: string;
  parcels: number;
  spendPence: Pence;
  /** Mean cost per parcel, whole pence */
  avgPence: Pence;
}

export interface AirBaseline {
  /** Single-product parcels with a known product size: the only ones whose air can be measured */
  parcelsMeasured: number;
  parcelVolumeLitres: number;
  productVolumeLitres: number;
  /** Share of the measured parcels' volume that is empty space, 0 to 1 */
  airShare: number;
}

export interface Baseline {
  parcels: number;
  spendPence: Pence;
  avgCostPence: Pence;
  medianCostPence: Pence;
  avgWeightKg: number;
  /** Mean parcel volume in litres (1 litre = 1,000 cm³) */
  avgVolumeLitres: number;
  byService: ServiceBaseline[];
  air: AirBaseline | null;
}

const litres = (l: number, w: number, h: number) => (l * w * h) / 1000;

export function baseline(parcels: Parcel[], products: ProductSpec[] = []): Baseline {
  const n = parcels.length;
  const spend = parcels.reduce((s, p) => s + p.chargedPence, 0);
  const costs = parcels.map((p) => p.chargedPence).sort((a, b) => a - b);
  const mid = Math.floor(n / 2);
  const median = n === 0 ? 0 : n % 2 ? costs[mid]! : Math.round((costs[mid - 1]! + costs[mid]!) / 2);

  const svc = new Map<string, { parcels: number; spend: number }>();
  for (const p of parcels) {
    // A service marked as not on any rate card keeps the client's own name for it.
    const key = p.carrierService.startsWith("UNPRICED:") ? p.carrierService.slice(9) : p.carrierService;
    const s = svc.get(key) ?? { parcels: 0, spend: 0 };
    s.parcels++;
    s.spend += p.chargedPence;
    svc.set(key, s);
  }
  const byService = [...svc]
    .map(([service, s]) => ({ service, parcels: s.parcels, spendPence: s.spend, avgPence: Math.round(s.spend / s.parcels) }))
    .sort((a, b) => b.spendPence - a.spendPence || a.service.localeCompare(b.service));

  const specs = new Map(products.map((s) => [s.sku, s]));
  let measured = 0;
  let parcelVol = 0;
  let productVol = 0;
  for (const p of parcels) {
    if (p.items.length !== 1 || p.items[0]!.qty !== 1) continue;
    const spec = specs.get(p.items[0]!.sku);
    if (!spec) continue;
    const pv = litres(p.dims.lengthCm, p.dims.widthCm, p.dims.heightCm);
    const sv = litres(spec.dims.lengthCm, spec.dims.widthCm, spec.dims.heightCm);
    // A product larger than its recorded parcel means bad data; leave it out rather than count negative air.
    if (sv > pv) continue;
    measured++;
    parcelVol += pv;
    productVol += sv;
  }

  return {
    parcels: n,
    spendPence: spend,
    avgCostPence: n ? Math.round(spend / n) : 0,
    medianCostPence: median,
    avgWeightKg: n ? round(parcels.reduce((s, p) => s + p.actualWeightKg, 0) / n, 3) : 0,
    avgVolumeLitres: n ? round(parcels.reduce((s, p) => s + litres(p.dims.lengthCm, p.dims.widthCm, p.dims.heightCm), 0) / n, 2) : 0,
    byService,
    air: measured
      ? { parcelsMeasured: measured, parcelVolumeLitres: round(parcelVol, 2), productVolumeLitres: round(productVol, 2), airShare: round(1 - productVol / parcelVol, 4) }
      : null,
  };
}
